/**
 * React entry point.
 *
 * Mounts `<App />` inside `<BrowserRouter>` and applies the global
 * Tailwind stylesheet. The `<StrictMode>` wrapper deliberately runs
 * effects twice in dev so we surface accidental subscription leaks
 * (e.g. forgotten Realtime channel unsubscribes) early.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { writeAccessKey } from './lib/agent-fetch';
import { runUserDataMigration } from './lib/userdata-migration';
import './index.css';

// Public-access build: allow one-time passcode hand-off via the URL
// (`?key=...`) for keyboard-less devices like the Pi kiosk. The key is
// persisted to localStorage and immediately scrubbed from the address
// bar / history (docs/SPEC-public-access.md).
{
  const params = new URLSearchParams(window.location.search);
  const urlKey = params.get('key');
  if (urlKey && urlKey.trim().length > 0) {
    writeAccessKey(urlKey);
    params.delete('key');
    const rest = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`,
    );
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Missing #root element in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);

// One-time browser → Pi user-data migration (SPEC-all-data-on-pi):
// uploads legacy localStorage data into empty `/api/userdata`
// namespaces, then flags itself done. Fire-and-forget after render —
// hooks fall back to the legacy values as defaults in the meantime,
// and an offline boot simply retries next time.
void runUserDataMigration();
